import { useState } from 'react'
import { PageHeader, Modal, FormField, Alert } from '../../components/ui'
import { btnPrimary, btnSecondary, colors } from '../../components/ui/styles'
import { useApiList } from '../../hooks/use-api-list'
import { api, ApiError } from '../../services/api'
import { relativeTime } from './fleet-helpers'
import {
  SUPPORT_REQUEST_STATUSES,
  SUPPORT_REQUEST_RESOLUTIONS,
  type TriageSupportRequestInput,
} from '@alpha-edge/shared'

interface SupportRequest {
  readonly id: string
  readonly organizationName: string | null
  readonly submitterName: string
  readonly submitterEmail: string
  readonly submitterPhone: string | null
  readonly subject: string
  readonly message: string
  readonly status: 'open' | 'in_progress' | 'completed'
  readonly resolution: 'merged_existing' | 'new_issue' | 'wont_fix' | null
  readonly followUpUrl: string | null
  readonly resolutionNotes: string | null
  readonly createdAt: string
}

const STATUS_LABELS: Record<SupportRequest['status'], string> = {
  open: 'Open',
  in_progress: 'In progress',
  completed: 'Completed',
}

const RESOLUTION_LABELS: Record<NonNullable<SupportRequest['resolution']>, string> = {
  merged_existing: 'Merged into existing issue',
  new_issue: 'New issue created',
  wont_fix: 'Closed — will not fix',
}

const STATUS_FILTERS = ['all', 'open', 'in_progress', 'completed'] as const

function statusBadge(status: SupportRequest['status']) {
  const palette: Record<SupportRequest['status'], { bg: string; fg: string }> = {
    open: { bg: '#fef3c7', fg: '#92400e' },
    in_progress: { bg: '#dbeafe', fg: '#1e40af' },
    completed: { bg: '#d1fae5', fg: '#065f46' },
  }
  const { bg, fg } = palette[status]
  return (
    <span
      style={{ background: bg, color: fg, padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 600 }}
    >
      {STATUS_LABELS[status]}
    </span>
  )
}

export function SupportRequestsPage() {
  const [statusFilter, setStatusFilter] = useState<(typeof STATUS_FILTERS)[number]>('all')
  const { data, total, page, loading, error, setPage, refresh } = useApiList<SupportRequest>({
    path: '/admin/support-requests',
    limit: 25,
    filters: { status: statusFilter === 'all' ? undefined : statusFilter },
  })
  const [selected, setSelected] = useState<SupportRequest | null>(null)

  return (
    <div>
      <PageHeader title="Support Requests" subtitle="Triage customer help requests submitted from the app." />

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatusFilter(s)}
            style={{
              ...(statusFilter === s ? btnPrimary : btnSecondary),
              padding: '0.25rem 0.75rem',
              fontSize: '0.8125rem',
              opacity: statusFilter === s ? 1 : 0.7,
            }}
          >
            {s === 'all' ? 'All' : STATUS_LABELS[s]}
          </button>
        ))}
      </div>

      {error && <Alert type="error" message={error} />}
      {loading ? (
        <div style={{ color: colors.textMuted, padding: '1rem' }}>Loading…</div>
      ) : data.length === 0 ? (
        <div style={{ color: colors.textMuted, padding: '1rem' }}>No support requests.</div>
      ) : (
        <div style={{ border: `1px solid ${colors.tableBorder}`, borderRadius: '6px', overflow: 'hidden' }}>
          {data.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => setSelected(r)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setSelected(r)
              }}
              style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.5fr 1fr 0.75fr',
                gap: '0.5rem',
                alignItems: 'center',
                padding: '0.5rem 0.75rem',
                borderTop: `1px solid ${colors.tableBorder}`,
                cursor: 'pointer',
                fontSize: '0.875rem',
              }}
            >
              <span
                style={{
                  fontWeight: 500,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.subject}
              </span>
              <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.organizationName ?? r.submitterName}
              </span>
              <span>{statusBadge(r.status)}</span>
              <span style={{ color: colors.textMuted, fontSize: '0.75rem' }}>{relativeTime(r.createdAt)}</span>
            </div>
          ))}
        </div>
      )}

      {total > 25 && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', alignItems: 'center' }}>
          <button type="button" style={btnSecondary} disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Prev
          </button>
          <span style={{ fontSize: '0.8125rem', color: colors.textMuted }}>
            Page {page} of {Math.ceil(total / 25)}
          </span>
          <button
            type="button"
            style={btnSecondary}
            disabled={page >= Math.ceil(total / 25)}
            onClick={() => setPage(page + 1)}
          >
            Next
          </button>
        </div>
      )}

      {selected && (
        <TriageModal
          request={selected}
          onClose={() => setSelected(null)}
          onSaved={() => {
            setSelected(null)
            refresh()
          }}
        />
      )}
    </div>
  )
}

function TriageModal({
  request,
  onClose,
  onSaved,
}: {
  request: SupportRequest
  onClose: () => void
  onSaved: () => void
}) {
  const [status, setStatus] = useState<SupportRequest['status']>(request.status)
  const [resolution, setResolution] = useState<string>(request.resolution ?? '')
  const [followUpUrl, setFollowUpUrl] = useState(request.followUpUrl ?? '')
  const [notes, setNotes] = useState(request.resolutionNotes ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      const body: TriageSupportRequestInput = {
        status,
        resolution: resolution ? (resolution as NonNullable<SupportRequest['resolution']>) : null,
        followUpUrl: followUpUrl.trim() || null,
        resolutionNotes: notes.trim() || null,
      }
      await api.patch(`/admin/support-requests/${request.id}`, body)
      onSaved()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={request.subject} width="560px">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', fontSize: '0.875rem' }}>
        <div style={{ color: colors.textMuted }}>
          From <strong>{request.submitterName}</strong> ({request.submitterEmail}
          {request.submitterPhone ? `, ${request.submitterPhone}` : ''})
          {request.organizationName ? ` · ${request.organizationName}` : ''}
        </div>
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            background: colors.bgMuted,
            padding: '0.75rem',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            margin: 0,
            fontFamily: 'inherit',
          }}
        >
          {request.message}
        </pre>

        {error && <Alert type="error" message={error} />}

        <FormField name="triage-status" label="Status">
          <select
            id="triage-status"
            value={status}
            onChange={(e) => setStatus(e.target.value as SupportRequest['status'])}
            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
          >
            {SUPPORT_REQUEST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField name="triage-resolution" label="Resolution (when completing)">
          <select
            id="triage-resolution"
            value={resolution}
            onChange={(e) => setResolution(e.target.value)}
            style={{ width: '100%', padding: '0.4rem', borderRadius: '4px', border: `1px solid ${colors.border}` }}
          >
            <option value="">—</option>
            {SUPPORT_REQUEST_RESOLUTIONS.map((r) => (
              <option key={r} value={r}>
                {RESOLUTION_LABELS[r]}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          name="triage-followup"
          label="Follow-up URL (e.g. GitHub issue)"
          value={followUpUrl}
          onChange={setFollowUpUrl}
          placeholder="https://github.com/MedFlowLLC/AlphaEdge/issues/…"
        />
        <FormField name="triage-notes" label="Triage notes" multiline rows={3} value={notes} onChange={setNotes} />

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" style={btnPrimary} onClick={save} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
